import * as React from 'react';

import { View, ViewProps, WBText } from '@/core';
import styles from './style';

export interface CardProps {
	/**
	 * Slot content to be displayed within the card, could be any type of element.
	 */
	children?: React.ReactNode;
	/**
	 * Title explaining the card's content.
	 */
	title: string;
	/**
	 * Component attached at the end of the title.
	 */
	titleEnd: React.ReactNode;
	/**
	 * Subtitle giving extra context to the card's content.
	 */
	subtitle: string;
	/**
	 * An object to style the outside container of the component.
	 */
	style?: ViewProps['style'];
}

/**
 * Cards are components that display content and actions on a single topic.
 *
 * The card component creates a blank canvas, contained by a thin grey border, that accepts any type of content in the form of
 *  slot <children> and can display an optional <title> or <subtitle>.
 *
 * Example usecases:
 * - Cards to display desks slot availability on a per-zone basis.
 *
 * @example
 * <WBCard title='WBCard example'>
 *     <WBText>This is an example on how to use a card.</WBText>
 *     <WBText>A card accepts multiple content children.</WBText>
 * </WBCard>
 */
export const WBCard: React.FC<CardProps> = ({ children, title = '', titleEnd, subtitle = '', style }) => {
	return (
		<View style={[styles.card, style]}>
			<View style={styles.header}>
				<View style={styles.title}>
					{!!title && (
						<View style={styles.titleStart}>
							<WBText style={styles.titleStartText}>{title}</WBText>
						</View>
					)}
					{!!titleEnd && <View style={styles.titleEnd}>{titleEnd}</View>}
				</View>
				{!!subtitle && <WBText style={styles.subtitle}>{subtitle}</WBText>}
			</View>
			<View style={styles.titleEnd}></View>
			<View style={styles.content}>
				<View style={styles.valueWrapper}>{children}</View>
			</View>
		</View>
	);
};
